import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { OutstandingRow } from '@/api/outstanding';
import type { Column } from '@/components/ui/Table';
import { cn } from '@/lib/cn';
import { interviewUrl } from '@/pages/Interview/interviewUrls';
import { type } from '@/theme/typography';

/**
 * **One outstanding row, rendered the same way in the two places it appears** — `DESIGN.md` §6.2
 * Section 1 and the outstanding-work screen this folder holds.
 *
 * A reader who follows *View all →* must meet the same table they were just looking at, not a second
 * description of it (TASK-157). The row is the same generated type on both screens because the backend
 * reuses `OldestOutstandingRow` for both payloads, so the columns can be one hook rather than two
 * lists that agree today.
 *
 * ⚠️ **No draft content, on either screen** (ADR 0006): a candidate, a vacancy, an interviewer, an age
 * and a link. The API sends nothing else and nothing here may display something that looks like it.
 */

/**
 * §6.2: *"6 or more days renders in `negative`"* — **the number, once**.
 *
 * The aging bands beside the hero take the same threshold from the API's own third bucket (`6+`, always
 * last, always in that order), so the band never spells a number; this is the numeric half of the one
 * rule, and it is exported so the screen that has no bands beside it still uses it rather than typing
 * `6` again.
 */
export const OUTSTANDING_AGE_THRESHOLD_DAYS = 6;

/** Whether an age has crossed §6.2's aging threshold and reads in `negative`. */
export const isOverdue = (ageDays: number): boolean => ageDays >= OUTSTANDING_AGE_THRESHOLD_DAYS;

/**
 * §6.2's four columns, **in the reference's order**: candidate, vacancy, interviewer, age.
 *
 * ⚠️ **The order was `interviewer · candidate · vacancy · age` until 2026-08-12**, because §6.2 stated
 * two orders at once — its sketch has read candidate-first since the section was written, and the
 * bullet under it said otherwise — and the implementation followed the bullet. **Corrected, not
 * re-decided**: nobody ever chose between them, and the reference settles it in the sketch's favour.
 *
 * ⚠️ **The link moves with the primary column**, which is now the candidate. §6.2's *"rows link to the
 * interview"* lands on the primary cell because `Table` owns every `<tr>` and exposes no row ref
 * (Sprint 10's recorded impossibility), and the accessible name carries that column's value **and**
 * what activating it does — *"link, Bogdan Ivanov"* is the cell read out with its purpose removed (§9).
 *
 * ⚠️ **The link is right even where it lands on a screen with nothing on it.** An `Interviewer`'s rows
 * are their own drafts and open §6.6, the form they owe; a `Recruiter`'s and an `Admin`'s point at
 * somebody else's draft, which answers with the blinded state — metadata they already have in front of
 * them on this very row, and never its contents (ADR 0006). A link to the honest answer beats a row
 * that is clickable for one group and dead for another.
 */
export function useOutstandingColumns(): Array<Column<OutstandingRow>> {
  const { t } = useTranslation('dashboard');

  return [
    {
      key: 'candidate',
      header: t('columns.candidate'),
      primary: true,
      cell: (row) => (
        <Link
          to={interviewUrl(row.interview_id)}
          aria-label={t('oldestRowLink', { candidate: row.candidate })}
          className="rounded hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          {row.candidate}
        </Link>
      ),
    },
    { key: 'vacancy', header: t('columns.vacancy'), cell: (row) => row.vacancy },
    { key: 'interviewer', header: t('columns.interviewer'), cell: (row) => row.interviewer },
    {
      key: 'age',
      header: t('columns.age'),
      align: 'right',
      /**
       * The age with its unit **glued** — `8d`, never `8` and `days` (§6.2, 2026-08-12, from the
       * reference).
       *
       * ⚠️ **The glue is visual, not semantic.** `d` read aloud is a letter, so the glued form is
       * hidden from assistive technology and the words are beside it — the same treatment Section 2's
       * `38h` takes, and for the same reason.
       */
      cell: (row) => (
        <span
          className={cn(
            type.numeral,
            'text-sm',
            isOverdue(row.age_days) ? 'text-negative' : 'text-ink-700',
          )}
        >
          <span aria-hidden>{t('age.glued', { count: row.age_days })}</span>
          <span className="sr-only">{t('age.spoken', { count: row.age_days })}</span>
        </span>
      ),
    },
  ];
}
